<script lang="ts">
import ErrorsMixin from '@/components/Mixins/ErrorsMixin.vue';
import LogsMixin from '@/components/Mixins/LogsMixin.vue';
import ComponentMixin from '@/components/Mixins/ComponentMixin.vue';
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { FieldError } from 'vee-validate';
import { EventBus, EventBusEvents } from '@/helpers/event-bus';

@Component({
  components: {
  }
})
export default class FieldMixin extends Mixins(ComponentMixin, ErrorsMixin, LogsMixin) {

  @Prop({ default: undefined }) // TO OVERRIDE
  public readonly input: any | undefined;

  @Prop({ type: [String], default: undefined })
  public readonly inputName: string | undefined;

  @Prop({ type: [String], default: undefined })
  public readonly inputPath: string | undefined;

  @Prop({ type: [String], default: undefined })
  public readonly label: string | undefined;

  @Prop({ type: [Boolean], default: true })
  public readonly outlined!: boolean;

  @Prop({ type: [Boolean], default: true })
  public readonly centered!: boolean;

  @Prop({ type: [Boolean], default: true })
  public readonly dense!: boolean;

  @Prop({ required: false })
  public readonly validatorTrigger: any;

  @Prop({ type: [Boolean], default: undefined })
  public readonly readonly: boolean | undefined;

  @Prop({ type: [Boolean], default: undefined })
  public readonly disabled: boolean | undefined;

  @Prop({ type: [Boolean], default: () => true })
  public readonly hideDetails: boolean | undefined;

  protected localInput: any | null = null;

  protected addedErrorKey: string | null = null;
  protected eventBus: EventBus = EventBus;

  // fieldName is an identifier for errors specific to the field.
  get fieldName(): string {
    return `${this.fullPath || 'any'}_field`;
  }

  get alias(): string | undefined {
    return this.inputName;
  }

  // addedName is an identifier for errors added by EventBus.
  get addedName(): string {
    return `${this.fullPath || 'any'}_added`;
  }

  get addedError(): string | null {
    return this.addedErrorKey && this.$t(this.addedErrorKey).toString();
  }

  // aggregate inputName and fieldName error messages.
  get errorMessages(): FieldError[] {
    const fieldMessages = (this.$i18n.locale && this.errorBag.collect(this.fieldName)) || [];
    const inputMessages = this.fullPath ? this.errorBag.collect(this.fullPath) : [];
    const addedMessages = this.errorBag.collect(this.addedName) || [];
    return fieldMessages.concat(inputMessages).concat(addedMessages);
  }

  get forceError(): boolean {
    return this.successState === false;
  }

  // Emit the field state : true = valid, null = not defined, false = some errors
  get fieldState(): boolean | null {
    if (this.successState !== undefined) {
      return this.successState;
    }
    if (this.errorMessages.length) {
      return false;
    }
    if (this.isNotDefined(this.localInput)) {
      return null;
    }
    return true;
  }

  // The field has a correct value.
  get success(): boolean {
    return this.fieldState === true;
  }

  get fullPath(): string | undefined {
    return this.inputPath ? `${this.inputPath}.${this.inputName}` : this.inputName;
  }

  // METHOD =======================================================
  protected validateField() {
    this.$nextTick(() => {
      this.removeError(this.fieldName);
      this.$validator.validate(this.fullPath);
    });
  }

  protected addErrorMessage(errorKey: string | null) {
    this.addedErrorKey = errorKey;
  }

  // HOOKS =======================================================
  @Watch('input', { immediate: true })
  protected onInputChange() {
    if (this.input === this.localInput) {
      return;
    }
    this.localInput = this.input === undefined ? null : this.input;
  }

  @Watch('fieldState', { immediate: true })
  protected onFieldStateChange() {
    this.$emit('fieldStateChange', this.fieldState);
  }

  @Watch('validatorTrigger')
  protected onValidatorTriggerChange() {
    this.validateField();
  }

  @Watch('addedError', { immediate: true })
  protected computeAddedErrors() {
    this.removeError(this.addedName);
    if (this.addedError) {
      this.addError(this.addedName, this.addedError);
    }
  }

  protected created() {

    this.eventBus.$on(EventBusEvents.controlAllFields, this.validateField);
    this.eventBus.$on(EventBusEvents.getControlFieldEvent(this.fullPath || ''), this.validateField);
    this.eventBus.$on(EventBusEvents.getAddErrorToFieldEvent(this.fullPath || ''), this.addErrorMessage);
  }
  public mounted() {
    this.eventBus.$emit(EventBusEvents.mustControlMyself, this.fullPath);
  }
  protected beforeDestroy() {
    this.eventBus.$off(EventBusEvents.controlAllFields, this.validateField);
    this.eventBus.$off(EventBusEvents.getControlFieldEvent(this.fullPath || ''), this.validateField);
    this.eventBus.$off(EventBusEvents.getAddErrorToFieldEvent(this.fullPath || ''), this.addErrorMessage);
  }
}
</script>

<style lang="stylus">
</style>
